import type { GradePublic } from '@repo/types/grades';

import { useGetAssignment } from '@repo/api/assignment/useGetAssignment';
import { useGetAssignmentSubmissions } from '@repo/api/assignment/useGetAssignmentSubmissions';
import { useGetGrade } from '@repo/api/grade/useGetGrade';
import { useGetQuizPublic } from '@repo/api/quiz/useGetQuizPublic';
import { cn } from '@repo/lib/cn';
import { format } from 'date-fns';
import { AlertCircleIcon, ArrowLeftIcon, CalendarIcon, CheckCircleIcon, CircleSlash, ClockIcon, SendIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import { AssignmentAttemptsTable } from '@/components/features/assignments/AssignmentAttemptsTable';
import { AssignmentBuilderQuizCard } from '@/components/features/assignments/builder/AssignmentBuilderQuizCard';
import { AttachmentCard } from '@/components/features/files/attachment-card';
import { resolveGradeBand } from '@/components/features/grades/gradeStyle';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateFooter, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { Tiptap } from '@/components/ui/tiptap-editor';
import { postExtensionSet } from '@/lib/tiptap-extensions-sets';
import { useCredentials } from '@/store/auth-store';

export default function AssignmentSubmissionViewPage() {
  const navigate = useNavigate();
  const { id: currentUserId } = useCredentials();
  const { assignmentId: assignmentIdParam, submissionId: submissionIdParam } = useParams<{
    assignmentId?: string;
    submissionId?: string;
  }>();
  const assignmentId = assignmentIdParam ? Number(assignmentIdParam) : undefined;
  const isValidId = assignmentId !== undefined && !Number.isNaN(assignmentId);
  // No submissionId → list mode (all attempts); with id → that attempt's detail.
  const isDetailMode = submissionIdParam !== undefined;

  const assignmentQuery = useGetAssignment(isValidId ? assignmentId : undefined);
  const submissionsQuery = useGetAssignmentSubmissions(isValidId ? assignmentId : undefined);

  const ownSubmissions = useMemo(() => (submissionsQuery.data ?? []).filter((entry) => entry.userId === currentUserId), [submissionsQuery.data, currentUserId]);
  const selectedSubmission = isDetailMode ? ownSubmissions.find((entry) => entry.id === Number(submissionIdParam)) : undefined;

  const gradeQuery = useGetGrade(selectedSubmission?.gradeId ?? undefined);
  const grade = gradeQuery.data;

  // Quiz-backed attempts carry their result on the quiz side; the assignment
  // submission only holds the quizRespId to join on. Only needed for the list.
  const quizId = assignmentQuery.data?.quiz?.id;
  const quizPublicQuery = useGetQuizPublic(quizId, !isDetailMode);
  const quizAttempts = quizPublicQuery.data?.quizresps;

  if (!isValidId) {
    return (
      <div className="base-direction-col">
        <EmptyState>
          <EmptyStateCircles className="z-0" />
          <FeaturedIcon size="lg" className="z-10">
            <CircleSlash />
          </FeaturedIcon>
          <EmptyStateContent>
            <EmptyStateTitle>Nieprawidłowy adres</EmptyStateTitle>
            <EmptyStateDescription>Brak identyfikatora zadania.</EmptyStateDescription>
          </EmptyStateContent>
          <EmptyStateFooter>
            <Button variant="secondary" onClick={() => navigate(-1)}>
              <ArrowLeftIcon />
              Powrót
            </Button>
          </EmptyStateFooter>
        </EmptyState>
      </div>
    );
  }

  if (assignmentQuery.isError) {
    return (
      <div className="base-direction-col">
        <EmptyState>
          <EmptyStateCircles className="z-0" />
          <FeaturedIcon size="lg" className="z-10">
            <CircleSlash />
          </FeaturedIcon>
          <EmptyStateContent>
            <EmptyStateTitle>{assignmentQuery.errorMessage.title}</EmptyStateTitle>
            <EmptyStateDescription>{assignmentQuery.errorMessage.detail}</EmptyStateDescription>
          </EmptyStateContent>
          <EmptyStateFooter>
            <Button variant="secondary" onClick={() => navigate(-1)}>
              <ArrowLeftIcon />
              Powrót
            </Button>
          </EmptyStateFooter>
        </EmptyState>
      </div>
    );
  }

  if (assignmentQuery.isPending || submissionsQuery.isPending) {
    return (
      <div className="base-direction-col h-full overflow-hidden">
        <div className="bg-background viewport-height relative flex w-full flex-col overflow-hidden rounded-2xl border">
          <div className="border-border-primary border-b p-4">
            <Skeleton className="mb-2 h-7 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="space-y-4 p-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  const assignment = assignmentQuery.data;
  const usedAttempts = ownSubmissions.length;
  const attemptsLeft = assignment.maxAttempts === 0 ? Infinity : Math.max(0, assignment.maxAttempts - usedAttempts);
  const hasSubmission = ownSubmissions.length > 0;
  const isQuizAssignment = assignment.quiz != null;
  // Mirror the BE availability check (controllers/assigment.js:675).
  // assignment.maxAttempts only gates text submits; the quiz tracks its own attempts.
  const now = new Date();
  const withinWindow = (!assignment.available || new Date(assignment.available) <= now) && (!assignment.dueTo || new Date(assignment.dueTo) >= now);
  const hasAttemptsLeft = isQuizAssignment ? true : assignment.maxAttempts === 0 || attemptsLeft > 0;
  const canStart = withinWindow && hasAttemptsLeft;

  const podejsciaText = assignment.maxAttempts === 0 ? `Podejścia: ${usedAttempts}` : `Podejścia: ${usedAttempts} / ${assignment.maxAttempts}`;

  return (
    <div className="base-direction-col h-full overflow-hidden">
      <div className="bg-background viewport-height relative flex w-full flex-col overflow-hidden rounded-2xl border">
        {/* Header */}
        <header className="border-border-primary flex flex-col gap-3 border-b p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex flex-col">
              <h1 className="text-2xl font-semibold">{assignment.name}</h1>
              <p className="text-muted-foreground text-sm">{isDetailMode ? 'Podgląd podejścia' : hasSubmission ? 'Twoje podejścia' : 'Brak zapisanej odpowiedzi'}</p>
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {canStart && !isDetailMode && (
                <Button variant="primary" onClick={() => navigate(`/assignments/${assignmentId}/resolve`)}>
                  <SendIcon />
                  {hasSubmission ? 'Kolejne podejście' : 'Rozpocznij'}
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {assignment.available && (
              <Badge variant="muted" border>
                <ClockIcon />
                Dostępne od {format(assignment.available, 'dd.MM.yyyy HH:mm')}
              </Badge>
            )}
            {assignment.dueTo && (
              <Badge variant="muted" border>
                <CalendarIcon />
                Termin {format(assignment.dueTo, 'dd.MM.yyyy HH:mm')}
              </Badge>
            )}
            <Badge variant="muted" border>
              {podejsciaText}
            </Badge>
            {hasSubmission && (
              <Badge variant="brand" border>
                <CheckCircleIcon />
                Przesłano
              </Badge>
            )}
          </div>
        </header>

        <div className="grid flex-1 grid-cols-1 gap-0 overflow-auto md:grid-cols-[minmax(0,1fr)_minmax(300px,340px)]">
          {/* Left column: grade + description + answer */}
          <div className="md:border-border-primary w-full space-y-8 p-4 md:border-r md:border-b-0">
            {isDetailMode && selectedSubmission && (
              <section className="space-y-3">
                <h5 className="text-base font-semibold">Ocena</h5>
                {selectedSubmission.gradeId == null ? (
                  <Alert>
                    <AlertCircleIcon />
                    <AlertTitle>Brak oceny</AlertTitle>
                    <AlertDescription>Twoja odpowiedź została przesłana. Wynik pojawi się po ocenie przez nauczyciela.</AlertDescription>
                  </Alert>
                ) : grade ? (
                  <GradeResultAlert grade={grade} />
                ) : null}
              </section>
            )}

            {assignment.desc && (
              <section className="space-y-2">
                <h5 className="text-base font-semibold">Treść zadania</h5>
                <Tiptap extensions={postExtensionSet} editable={false} content={assignment.desc} />
              </section>
            )}

            {isDetailMode ? (
              <section className="space-y-4">
                <h5 className="text-base font-semibold">Twoja odpowiedź</h5>
                {selectedSubmission ? (
                  <>
                    {selectedSubmission.body ? (
                      <Tiptap extensions={postExtensionSet} editable={false} content={selectedSubmission.body} />
                    ) : (
                      <p className="text-muted-foreground text-sm italic">Brak treści odpowiedzi.</p>
                    )}
                    {selectedSubmission.files.length > 0 && (
                      <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                        {selectedSubmission.files.map((file) => (
                          <AttachmentCard key={file.id} id={file.id} name={file.name} />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm italic">Nie znaleziono podejścia.</p>
                )}
              </section>
            ) : (
              <section className="space-y-3">
                <h5 className="text-base font-semibold">Twoje podejścia</h5>
                <AssignmentAttemptsTable assignmentId={assignment.id} submissions={ownSubmissions} quiz={assignment.quiz} quizAttempts={quizAttempts} autoGrade={assignment.autoGrade} />
              </section>
            )}
          </div>

          {/* Right column: quiz + teacher files */}
          <div className="space-y-8 p-4">
            {assignment.quiz && (
              <section className="space-y-3">
                <h5 className="text-base font-semibold">Quiz</h5>
                <AssignmentBuilderQuizCard quiz={assignment.quiz} />
              </section>
            )}
            <section className="space-y-3">
              <h5 className="text-base font-semibold">Pliki od nauczyciela</h5>
              {assignment.files.length === 0 ? (
                <p className="text-muted-foreground text-xs italic">Brak załączonych plików.</p>
              ) : (
                <div className="grid grid-cols-1 gap-2">
                  {assignment.files.map((file) => (
                    <AttachmentCard key={file.id} id={file.id} name={file.name} interactive={false} />
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

interface GradeResultAlertProps {
  grade: GradePublic;
}

/**
 * Graded-result banner, tinted with the grade's ramp color — a mapped grade
 * takes its band's color, a raw percentage its own ramp position. Only a
 * free-form grade the origin scale can't resolve falls back to the neutral
 * success styling.
 */
function GradeResultAlert({ grade }: GradeResultAlertProps) {
  const { display, color } = resolveGradeBand(grade);

  const trimmedDesc = grade.desc?.trim();
  const description = trimmedDesc?.toLowerCase().startsWith('auto-graded from quiz')
    ? 'Oceniono automatycznie na podstawie wyniku quizu.'
    : (trimmedDesc ?? 'Twoja odpowiedź została oceniona przez nauczyciela.');

  return (
    <Alert
      variant={color ? 'default' : 'success'}
      style={
        color
          ? {
              backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`,
              borderColor: `color-mix(in srgb, ${color} 40%, transparent)`,
            }
          : undefined
      }
    >
      <CheckCircleIcon style={color ? { color } : undefined} />
      <AlertTitle>
        <span
          className={cn('inline-block rounded-md px-1.5 py-0.5 text-sm font-semibold', !color && 'bg-success/20 text-text-success')}
          style={color ? { backgroundColor: color, color: `contrast-color(${color})` } : undefined}
        >
          {display}
        </span>
      </AlertTitle>
      <AlertDescription>{description}</AlertDescription>
    </Alert>
  );
}
